import { useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import type { EventShare, FriendGroup } from "@/api/bale.zod";

import {
  useDeleteEventShare,
  useGetFriendGroup,
  useGetProfileSuspense,
  useListEventSharesSuspense,
  useListFriendGroupsSuspense,
  useUpdateEventShare,
} from "@/api/bale";
import { ActionBox } from "@/components/ActionBox";
import { Button } from "@/components/Button";
import { DegreeOfSeparationInput } from "@/components/DegreeOfSeparationInput";
import { Dropdown } from "@/components/Dropdown";
import { Icon } from "@/components/Icon";
import { IconButton } from "@/components/IconButton";
import { Input } from "@/components/Input";
import { MenuField } from "@/components/MenuField";
import { MenuItem } from "@/components/MenuItem";
import { Missing } from "@/components/Missing";

import { useCurrentEventSuspense } from "../../../../hooks/useCurrentEvent";

export function SharesSection() {
  const { t } = useTranslation();
  const id = useId();
  const popover = useRef<HTMLDivElement>(null);
  const [search, setSearch] = useState("");
  const event = useCurrentEventSuspense({ query: { select: (data) => data.data.event } });
  const me = useGetProfileSuspense("@me", { query: { select: (data) => data.data.profile } });
  const shares = useListEventSharesSuspense(event.data.id, {
    query: {
      select: (data) => data.data.event_shares.filter((share) => share.account_id === me.data.id),
    },
  });
  const alreadyShared = new Set(shares.data.map((share) => share.friend_group_id));
  const friendGroups = useListFriendGroupsSuspense({ query: { select: (data) => data.data.friend_groups } });
  const filteredGroups = friendGroups.data
    .filter((group) => !alreadyShared.has(group.id))
    .filter((group) => group.name.toLowerCase().startsWith(search.toLowerCase()));
  const updateShare = useUpdateEventShare();

  function shareToGroup(group: FriendGroup) {
    updateShare.mutate({ eventId: event.data.id, friendGroupId: group.id, data: { discoverable_distance: 0 } });
    popover.current?.hidePopover();
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-4 px-4">
        <h3 className="font-interest">{t("share-to-friend-groups")}</h3>
        <p className="font-disinterest">{t("share-to-friend-groups-tutorial")}</p>
        {shares.data.length === 0 && <Missing>{t("event-not-sharing")}</Missing>}
      </div>
      <div className="flex flex-col border-divider not-empty:border-b">
        {shares.data.map((share) => (
          <EventShareItem key={share.friend_group_id} share={share} />
        ))}
      </div>
      <div className="px-4">
        <Button type="button" popoverTarget={id} className="flex flex-row items-center justify-between gap-2">
          <Icon name="plus" />
          {t("share-to-friend-group-action")}
        </Button>
      </div>
      <Dropdown
        id={id}
        popover="auto"
        className="absolute -ml-2 w-max min-w-60 [position-area:right_span-bottom]"
        ref={popover}
        onToggle={() => setSearch("")}
      >
        <MenuField>
          <Icon name="search" />
          <Input
            type="search"
            placeholder={t("search")}
            value={search}
            onChange={(event) => setSearch(event.currentTarget.value)}
            autoFocus
          />
        </MenuField>
        {filteredGroups.map((group) => (
          <MenuItem key={group.id} onClick={() => shareToGroup(group)}>
            {group.name}
          </MenuItem>
        ))}
        {filteredGroups.length === 0 && <MenuItem disabled>{t("share-no-groups-left")}</MenuItem>}
      </Dropdown>
    </div>
  );
}

function EventShareItem({ share }: { share: EventShare }) {
  const updateShare = useUpdateEventShare();
  const deleteShare = useDeleteEventShare();
  const event = useCurrentEventSuspense({ query: { select: (data) => data.data } });
  const me = useGetProfileSuspense("@me", { query: { select: (data) => data.data.profile } });
  const friendGroup = useGetFriendGroup(share.friend_group_id, {
    query: { select: (data) => data.data.friend_group },
  });
  const participantMe = event.data.event_participants.find((participant) => participant.profile.id === me.data.id);
  return (
    <div className="flex flex-row items-center justify-between border-t border-divider px-4 py-2">
      <p className="font-interest">{friendGroup.data?.name}</p>
      <ActionBox>
        <DegreeOfSeparationInput
          naming="invitability"
          defaultValue={share.discoverable_distance}
          max={participantMe?.limiting_discoverable_distance}
          onChange={(distance) =>
            updateShare.mutate({
              eventId: event.data.event.id,
              friendGroupId: share.friend_group_id,
              data: { discoverable_distance: distance },
            })
          }
        />
        <IconButton
          type="button"
          name="trash"
          onClick={() => deleteShare.mutate({ eventId: event.data.event.id, friendGroupId: share.friend_group_id })}
          className="text-danger"
        />
      </ActionBox>
    </div>
  );
}
